import { useQuery, UseQueryOptions } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ScheduledPostTarget } from 'types'
import { ScheduledPostTargetsClient } from 'api/clients/ScheduledPostTargetsClient'
import { scheduledPostTargetsKeys } from './scheduledPostTargetsKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { MessageType } from 'antd/lib/message'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'

type Targets = ScheduledPostTarget[]

export interface Options<TData = Targets> extends UseQueryOptions<Targets, unknown, TData> {
  enablePolling?: boolean
}

export const fetchScheduledPostTargets = async ({
  workspaceId,
  scheduledPostId,
}: {
  workspaceId: number
  scheduledPostId: number
}) => {
  const response = await ScheduledPostTargetsClient.index({ workspaceId, scheduledPostId })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(response.data)
  }

  return response.data.scheduled_post_targets as Targets
}

const formatHumanList = (items: string[]): string => {
  const uniqueItems = Array.from(new Set(items.filter(Boolean)))
  if (uniqueItems.length === 0) return ''
  if (uniqueItems.length === 1) return uniqueItems[0]
  if (uniqueItems.length === 2) return `${uniqueItems[0]} and ${uniqueItems[1]}`
  const allButLast = uniqueItems.slice(0, -1).join(', ')
  const last = uniqueItems[uniqueItems.length - 1]
  return `${allButLast}, and ${last}`
}

const providerLabel = (provider: ExternalIntegrationEnum): string => ProviderToName[provider] ?? String(provider)

const isPublishingStatus = (status: ScheduledPostTarget['status']): boolean =>
  status === 'queued' || status === 'processing' || status === 'retrying'

const isSuccessfulStatus = (status: ScheduledPostTarget['status']): boolean => status === 'published'

const isFailedStatus = (status: ScheduledPostTarget['status']): boolean => status === 'failed'

type ToastPhase = 'idle' | 'publishing' | 'finalized'

type ToastRecord = {
  phase: ToastPhase
  close?: MessageType
  frozenProgressText?: string | null
  lastFinalText?: string | null
}

const toastRegistry = new Map<number, ToastRecord>()

const getToastRecord = (scheduledPostId: number): ToastRecord => {
  let record = toastRegistry.get(scheduledPostId)
  if (!record) {
    record = { phase: 'idle', close: undefined, frozenProgressText: null, lastFinalText: null }
    toastRegistry.set(scheduledPostId, record)
  }
  return record
}

const showProgressToastOnce = (scheduledPostId: number, progressText: string): void => {
  const record = getToastRecord(scheduledPostId)

  // If publishing already started, keep the original immutable text.
  if (record.phase === 'publishing') return

  record.frozenProgressText = progressText

  // Close any existing toast and open an indefinite one.
  record.close?.()
  record.close = showToastMessage(progressText, 'generating', undefined, 0) // 0 => indefinite
  record.phase = 'publishing'
}

const finalizeProgressToastOnce = (
  scheduledPostId: number,
  finalText: string | null,
  variant: 'success' | 'error',
): void => {
  const record = getToastRecord(scheduledPostId)

  if (record.phase === 'finalized') return

  // Close progress toast and clear session text.
  record.close?.()
  record.close = undefined
  record.frozenProgressText = null

  if (finalText && finalText.trim().length > 0 && record.lastFinalText !== finalText) {
    showToastMessage(finalText, variant)
    record.lastFinalText = finalText
  }

  record.phase = 'finalized'
}

const resetToastOnError = (scheduledPostId: number): void => {
  const record = toastRegistry.get(scheduledPostId)
  if (!record) return
  record.close?.()
  record.close = undefined
  record.frozenProgressText = null
  // Keep phase/lastFinalText so we don’t spam final toasts if errors repeat.
}

const buildProgressText = (targets: Targets): string => {
  const publishingPlatforms = targets
    .filter((target) => isPublishingStatus(target.status))
    .map((target) => providerLabel(target.provider))
    .sort((a, b) => a.localeCompare(b))

  const listText = formatHumanList(publishingPlatforms)
  return listText ? `Publishing to ${listText}` : 'Publishing…'
}

const buildFinalToastText = (targets: Targets): string | null => {
  const successfulPlatforms = targets
    .filter((target) => isSuccessfulStatus(target.status))
    .map((target) => providerLabel(target.provider))
    .sort((a, b) => a.localeCompare(b))

  const failedPlatforms = targets
    .filter((target) => isFailedStatus(target.status))
    .map((target) => providerLabel(target.provider))
    .sort((a, b) => a.localeCompare(b))

  const successText = successfulPlatforms.length ? `Published to ${formatHumanList(successfulPlatforms)}` : ''

  const failureText = failedPlatforms.length ? `failed to publish to ${formatHumanList(failedPlatforms)}` : ''

  if (successText && failureText) return `${successText}; ${failureText}.`
  if (successText) return `${successText}!`
  if (failureText) return `Oops—${failureText}.`
  return null
}

const shouldContinuePolling = <TData = unknown,>(maybeData?: TData): number | false => {
  // Only poll while any target is actively publishing.
  if (Array.isArray(maybeData)) {
    const targets = maybeData as unknown as Targets
    const anyPublishing = targets.some((t) => isPublishingStatus(t.status))
    return anyPublishing ? 1000 : false
  }
  return false
}

export const useScheduledPostTargets = <TData = Targets,>(scheduledPostId: number, options?: Options<TData>) => {
  const workspaceId = useCurrentWorkspaceId()
  const { enablePolling = false, ...queryOptions } = options || {}

  const queryKey = scheduledPostTargetsKeys.list(workspaceId, scheduledPostId)
  const queryFn = () => fetchScheduledPostTargets({ workspaceId, scheduledPostId })

  return useQuery<Targets, unknown, TData>(queryKey, queryFn, {
    refetchOnWindowFocus: false,
    refetchInterval: enablePolling ? shouldContinuePolling : false,

    onSuccess: (incoming) => {
      try {
        const targets = Array.isArray(incoming) ? (incoming as unknown as Targets) : []
        const anyPublishing = targets.some((t) => isPublishingStatus(t.status))
        const record = getToastRecord(scheduledPostId)

        if (anyPublishing) {
          if (record.phase === 'finalized') {
            record.phase = 'idle'
            record.lastFinalText = null
          }

          const progressText = buildProgressText(targets)
          showProgressToastOnce(scheduledPostId, progressText)
        } else {
          if (record.phase === 'publishing') {
            const finalText = buildFinalToastText(targets)
            const hasAnyFailed = targets.some((t) => isFailedStatus(t.status))
            const variant = hasAnyFailed ? 'error' : 'success'
            finalizeProgressToastOnce(scheduledPostId, finalText, variant)
          }
        }
      } finally {
        queryOptions.onSuccess?.(incoming)
      }
    },

    onError: (error) => {
      resetToastOnError(scheduledPostId)
      queryOptions.onError?.(error as any)
    },
    enabled: !!workspaceId && !!scheduledPostId,
    ...queryOptions,
  })
}
